import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, MessageCircle } from "lucide-react";
import RevealText, { RevealTextOnView } from "../components/RevealText";
import KitDownloadModal from "../components/KitDownloadModal";
import NewsletterSignup from "../components/NewsletterSignup";
import NewsletterPopup from "../components/NewsletterPopup";
import AdventHomeBanner from "../components/AdventHomeBanner";
import { ComeTiPossoAiutare } from "../components/ComeTiPossoAiutare";
import { BRAND } from "../lib/brand";

const MANIFESTO = [
  { n: "01", title: "Ascolto", body: "Ogni percorso comincia da uno spazio di ascolto vero, senza fretta, senza giudizio." },
  { n: "02", title: "Cura", body: "Metto al centro le persone. Non tecniche fredde, ma presenza calda e discreta." },
  { n: "03", title: "Fiducia", body: "Costruisco alleanze educative con genitori, coppie e famiglie. Un passo per volta." },
  { n: "04", title: "Famiglia", body: "La famiglia non è un problema da risolvere: è un sistema vivo che merita cura." },
];

const SERVICES_TEASER = [
  "Consulenza sonno",
  "Alimentazione complementare",
  "Metodo Montessori",
  "Feste ed eventi",
];

export default function Home() {
  const heroRef = useRef(null);
  const photoRef = useRef(null);
  const [kitOpen, setKitOpen] = useState(false);
  const { scrollYProgress } = useScroll({ target: photoRef, offset: ["start end", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <div className="pt-24 md:pt-28">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[95vh] px-6 md:px-10 pt-8 pb-24 overflow-hidden">
        <div className="max-w-[1600px] mx-auto relative">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
            className="font-editorial text-[#FF2768] text-xl md:text-2xl mb-4"
          >
            — Ciao, sono
          </motion.p>

          <h1 className="font-display text-[22vw] md:text-[16vw] lg:text-[13.5rem] xl:text-[16rem] leading-[0.82] text-[#3D2140]" data-testid="hero-heading">
            <RevealText text="Teresa" delay={0.1} />
            <RevealText text={[<>Vilardo</>]} delay={0.2} className="text-[#FF2768] block" />
          </h1>

          <div className="mt-6 md:mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="max-w-md"
            >
              <p className="text-lg text-[#3D2140]/80 leading-relaxed mb-6">
                <span className="font-semibold uppercase tracking-widest text-xs text-[#81C3C9]">Educatrice nei servizi per l'infanzia · Family Coach Montessori · Educatrice neonatale · Consulente in Puericultura 0–36 mesi</span><br/>
                Accompagno famiglie e tate nella cura dei più piccoli — sonno, alimentazione, gioco, Montessori — con sguardo delicato e strumenti concreti.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/prenota" data-testid="hero-cta-primary" className="btn-tv-primary">
                  Prenota consulenza <ArrowRight size={16} />
                </Link>
                <a href="https://forms.gle/PrgPmQzc55QgqbJR7" target="_blank" rel="noopener noreferrer" data-testid="hero-cta-info" className="btn-tv-outline">
                  Richiedi informazioni <ArrowRight size={16} />
                </a>
                <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#3D2140] hover:text-[#FF2768] transition-colors">
                  <MessageCircle size={16} /> Scrivimi su WhatsApp
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-[420px] md:max-w-[460px] mx-auto md:mx-0"
            >
              <div className="absolute inset-0 -m-6 bg-[#81C3C9]/25 rounded-[50%] blur-2xl" />
              <div className="relative aspect-[4/5] hero-photo-frame border-4 border-white shadow-2xl">
                <motion.img
                  ref={photoRef}
                  src={BRAND.photo}
                  alt="Teresa Vilardo"
                  style={{ y: photoY }}
                  className="w-full h-[110%] object-cover"
                />
              </div>
              {/* floating bear */}
              <motion.img
                src={BRAND.logo}
                alt=""
                aria-hidden
                style={{ rotate: bearRotate }}
                className="absolute -bottom-8 -left-6 h-24 w-24 md:h-32 md:w-32 rounded-full animate-floaty drop-shadow-xl"
              />
            </motion.div>
          </div>
        </div>

        {/* scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.4em] text-[#3D2140]/50"
        >
          scorri per scoprire ↓
        </motion.div>
      </section>

      {/* NATALE 2026 — visibile solo quando il Calendario dell'Avvento è attivo */}
      <AdventHomeBanner />

      {/* COME TI POSSO AIUTARE — carousel visibile subito alle famiglie */}
      <ComeTiPossoAiutare />

      {/* REGALO / KIT GRATUITO */}
      <section className="px-6 md:px-10 py-16 md:py-24" data-testid="regalo-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-[1200px] mx-auto"
        >
          <div className="relative overflow-hidden rounded-[32px] md:rounded-[48px] bg-gradient-to-br from-[#FF2768] via-[#FF2768] to-[#E01F5A] p-8 md:p-14 lg:p-20 shadow-2xl">
            {/* Decorative bear */}
            <img
              src={BRAND.logo}
              alt=""
              aria-hidden
              className="absolute -right-16 -top-16 md:-right-20 md:-top-20 h-64 w-64 md:h-96 md:w-96 opacity-15 rotate-12 pointer-events-none"
            />
            {/* Decorative blob */}
            <div className="absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-[#81C3C9]/30 blur-3xl pointer-events-none" />

            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
              <div className="md:col-span-8">
                <p className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.4em] text-white/90 mb-5 font-semibold">
                  Un regalo per te
                </p>
                <h2 className="font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9] mb-6">
                  Filastrocche<br/>delle ditina.
                </h2>
                <p className="text-white/90 text-base md:text-lg leading-relaxed max-w-xl">
                  Un piccolo dono pensato per <strong>i genitori</strong>, per trascorrere momenti speciali con il proprio bambino o bambina — da usare come routine prima della nanna, durante la cura o l'igiene. E per <strong>le tate</strong>, come strumento di relazione e divertimento durante le giornate di lavoro e gioco.
                </p>
              </div>

              <div className="md:col-span-4 flex md:justify-end">
                <button
                  type="button"
                  onClick={() => setKitOpen(true)}
                  data-testid="regalo-cta"
                  className="group inline-flex items-center gap-3 bg-white text-[#FF2768] rounded-full px-8 md:px-10 py-4 md:py-5 font-semibold text-base md:text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                >
                  Scarica il Kit gratuito
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-10 md:py-16 border-y border-[#81C3C9]/30 bg-white overflow-hidden" data-testid="marquee-section">
        <Marquee speed={40} gradient={false} pauseOnHover>
          {["Famiglia", "Cura", "Ascolto", "Fiducia", "Presenza", "Dialogo", "Riservatezza"].map((word, i) => (
            <span key={i} className="font-display text-[8vw] md:text-[6vw] px-8 flex items-center gap-8">
              <span className={i % 2 === 0 ? "text-[#3D2140]" : "marquee-outline"}>{word}</span>
              <span className="text-[#FF2768] text-4xl">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="manifesto-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Manifesto</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              <RevealTextOnView text={["Quattro parole", "che sono casa."]} />
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end">
            <p className="text-lg text-[#3D2140]/70 leading-relaxed font-editorial">
              Non ho un metodo unico. Ho un modo di stare accanto alle persone, che si ripete ogni volta con la stessa attenzione.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-20">
          {MANIFESTO.map((item, idx) => (
            <motion.div
              key={item.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: idx * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`${idx % 2 === 1 ? "md:mt-24" : ""}`}
            >
              <div className="flex items-baseline gap-6 mb-4">
                <span className="font-editorial text-[#FF2768] text-3xl">{item.n}</span>
                <div className="h-px flex-1 bg-[#3D2140]/15" />
              </div>
              <h3 className="font-display text-6xl md:text-7xl text-[#3D2140] mb-4">{item.title}</h3>
              <p className="text-[#3D2140]/70 text-lg leading-relaxed max-w-md">{item.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="relative px-6 md:px-10 py-24 md:py-32 bg-[#81C3C9]/25 overflow-hidden">
        <img src={BRAND.logo} alt="" aria-hidden className="absolute -right-24 -top-24 h-96 w-96 opacity-20 rotate-12" />
        <div className="max-w-[1600px] mx-auto relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-6">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Cosa posso fare per te</p>
              <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
                <RevealTextOnView text={["Percorsi su misura,", "per ogni famiglia."]} />
              </h2>
            </div>
          </div>

          <ul className="divide-y divide-[#3D2140]/15 border-y border-[#3D2140]/15">
            {SERVICES_TEASER.map((s, i) => (
              <li key={s}>
                <Link
                  to="/cosa-posso-fare-per-te"
                  data-testid={`service-teaser-${i}`}
                  className="group flex items-center justify-between py-8 md:py-10 hover:pl-4 transition-all duration-500"
                >
                  <span className="font-display text-4xl md:text-6xl text-[#3D2140] group-hover:text-[#FF2768] transition-colors">{s}</span>
                  <ArrowRight className="text-[#3D2140] group-hover:text-[#FF2768] group-hover:translate-x-2 transition-all" size={28} />
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-14">
            <Link to="/cosa-posso-fare-per-te" className="btn-tv-primary" data-testid="services-cta">
              Scopri cosa posso fare per te <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* GUIDA GRATUITA CTA */}
      <section className="px-6 md:px-10 pt-24 pb-8" data-testid="guida-cta-section">
        <Link
          to="/guida-gratuita"
          data-testid="home-guida-banner"
          className="group block max-w-[1600px] mx-auto relative overflow-hidden rounded-[40px] bg-[#3D2140] text-white p-10 md:p-16 hover:shadow-2xl transition-all duration-500"
        >
          <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-[#FF2768]/25 blur-3xl pointer-events-none" />
          <div className="absolute -left-16 -bottom-16 h-80 w-80 rounded-full bg-[#81C3C9]/20 blur-3xl pointer-events-none" />
          <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_brand-vilardo/artifacts/ovbpp0nb_logo%20cromie%20piene.png" alt="" aria-hidden className="absolute right-6 md:right-16 top-1/2 -translate-y-1/2 h-32 md:h-56 w-32 md:w-56 rounded-full opacity-90 animate-floaty" />
          <div className="relative max-w-2xl">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] mb-4 font-semibold">— Un dono per te</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.88] mb-6">
              Scarica la mia<br/><span className="text-[#FF2768]">guida gratuita.</span>
            </h2>
            <p className="text-white/80 text-lg leading-relaxed mb-8 max-w-lg">
              Contenuti pratici, sguardo rispettoso, coerente con il Metodo T.A.T.A.®. Riceverai il PDF via email dopo un semplice form.
            </p>
            <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF2768] text-white font-semibold group-hover:gap-4 transition-all">
              Scarica ora <ArrowRight size={16} />
            </span>
          </div>
        </Link>
      </section>

      {/* NEWSLETTER — Una lettera alla volta */}
      <NewsletterSignup />

      {/* QUOTE */}
      <section className="px-6 md:px-10 py-32 max-w-[1400px] mx-auto text-center" data-testid="quote-section">
        <p className="font-editorial text-3xl md:text-5xl lg:text-6xl text-[#3D2140] leading-tight">
          <span className="text-[#FF2768]">“</span>Ogni famiglia ha una musica sua.<br/>
          Il mio lavoro è aiutarti ad ascoltarla, insieme<span className="text-[#FF2768]">.”</span>
        </p>
        <p className="mt-8 text-sm uppercase tracking-[0.4em] text-[#81C3C9]">— Teresa Vilardo</p>
      </section>

      {/* Kit Download Modal */}
      <KitDownloadModal open={kitOpen} onClose={() => setKitOpen(false)} />

      {/* Newsletter popup (appears after 30s, only on Home) */}
      <NewsletterPopup />
    </div>
  );
}
